import { ActionIcon, Button, Menu, ScrollArea, Text, Tooltip, UnstyledButton } from "@mantine/core";
import { useClipboard } from "@mantine/hooks";
import { useQueryClient } from "@tanstack/react-query";
import {
  EllipsisVertical,
  FolderInput,
  FolderPlus,
  LayoutGrid,
  Link as LinkIcon,
  Pencil,
  Plus,
  Star,
  Trash2,
} from "lucide-react";
import { useMemo, useState } from "react";

import {
  getListFoldersQueryKey,
  useAddFolderFavorite,
  useCreateFolder,
  useListFolders,
  useRemoveFolderFavorite,
  useRenameFolder,
} from "../../api/generated/endpoints/teams/teams";
import type { Folder } from "../../api/generated/model";
import { refreshLibraryLists } from "../../api/library-lists";
import { ErrorAlert } from "../../platform/ErrorAlert";
import { folderTree } from "../../platform/folders/folder-tree";
import { FolderNameField, FolderTreeList, type FolderEdit } from "../../platform/folders/FolderTreeList";
import { useMembership } from "../../platform/membership";
import { canEditContent } from "../../platform/permissions";
import { DeleteFolderDialog, MoveFolderDialog } from "./FolderDialogs";
import classes from "./library.module.css";

interface FolderPanelProps {
  teamId: string;
  brandId: string;
  /** The URL's folder; `undefined` is "All assets". */
  folderId: string | undefined;
  onChoose: (folderId: string | undefined) => void;
  /** The header's star (`FavoritesToggle`): the favourites alone, flat. */
  favorites: boolean;
  onFavorites: (favorites: boolean) => void;
}

/**
 * Live's favourites star beside "Projects" in the header row while the panel is open
 * (`FavoriteFilterButton`, #784): the panel then shows the favourites alone, flat.
 */
export function FavoritesToggle({ on, onChange }: { on: boolean; onChange: (on: boolean) => void }) {
  return (
    <Tooltip label={on ? "Show every project" : "Show favourites only"}>
      <ActionIcon variant="subtle" aria-label="Favourites only" aria-pressed={on} onClick={() => onChange(!on)}>
        <Star size={16} fill={on ? "currentColor" : "none"} aria-hidden />
      </ActionIcon>
    </Tooltip>
  );
}

/**
 * The library's projects (spec A3b, design-ref/folders-410): "All assets", then the brand's
 * folders as live's tree, each row with a star for favourites and live's menu (Rename, Copy link,
 * Move, Add subproject, Add to favourites, Delete); "Create Project" at the end, the last thing in
 * the panel's own scroll (#784). The header row's star (`FavoritesToggle`) shows the favourites
 * alone, flat, as live's does.
 */
export function FolderPanel(props: FolderPanelProps) {
  const { teamId, brandId, folderId, onChoose, favorites, onFavorites } = props;
  const queryClient = useQueryClient();
  const me = useMembership(teamId);
  const folders = useListFolders(teamId, brandId);
  const [editing, setEditing] = useState<FolderEdit | undefined>(undefined);
  const [creating, setCreating] = useState(false);
  const [menuFor, setMenuFor] = useState<string | undefined>(undefined);
  const [moving, setMoving] = useState<Folder | undefined>(undefined);
  const [deleting, setDeleting] = useState<Folder | undefined>(undefined);
  const clipboard = useClipboard({ timeout: 2000 });
  const [copied, setCopied] = useState<string | undefined>(undefined);

  const tree = useMemo(() => folderTree(folders.data ?? []), [folders.data]);
  // Live's favourites filter: every favourite, flat, without its subprojects.
  const favoriteTree = useMemo(
    () =>
      folderTree(
        (folders.data ?? [])
          .filter((each) => each.is_favorite)
          .map((each) => ({ ...each, parent_id: null })),
      ),
    [folders.data],
  );
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: getListFoldersQueryKey(teamId, brandId) });
  const ids = { teamId, brandId };
  const create = useCreateFolder({
    mutation: {
      onSuccess: async () => {
        setCreating(false);
        setEditing(undefined);
        await refresh();
      },
    },
  });
  const rename = useRenameFolder({
    mutation: {
      onSuccess: async () => {
        setEditing(undefined);
        await refresh();
      },
    },
  });
  const favorite = useAddFolderFavorite({ mutation: { onSettled: refresh } });
  const unfavorite = useRemoveFolderFavorite({ mutation: { onSettled: refresh } });
  const toggleFavorite = (folder: Folder) =>
    (folder.is_favorite ? unfavorite : favorite).mutate({ ...ids, folderId: folder.id });
  const mayChange = (folder: Folder) =>
    me !== undefined && canEditContent(me.role, folder.created_by, me.userId);
  const failed = create.error ?? rename.error ?? favorite.error ?? unfavorite.error;

  const copyLink = (folder: Folder) => {
    const url = new URL(window.location.href);
    url.search = new URLSearchParams({ folder: folder.id }).toString();
    clipboard.copy(url.toString());
    setCopied(folder.id);
  };

  const star = (folder: Folder) => (
    <Tooltip label={folder.is_favorite ? "Remove from favourites" : "Add to favourites"}>
      <ActionIcon
        variant="transparent"
        size="xs"
        aria-label={`${folder.is_favorite ? "Remove" : "Add"} ${folder.name} ${folder.is_favorite ? "from" : "to"} favourites`}
        aria-pressed={folder.is_favorite}
        onClick={() => toggleFavorite(folder)}
      >
        <Star size={16} fill={folder.is_favorite ? "currentColor" : "none"} aria-hidden />
      </ActionIcon>
    </Tooltip>
  );

  const menu = (folder: Folder) => (
    <Menu
      position="bottom-start"
      withinPortal
      opened={menuFor === folder.id}
      onChange={(opened) => setMenuFor(opened ? folder.id : undefined)}
    >
      <Menu.Target>
        <ActionIcon variant="subtle" size="sm" aria-label={`Actions for ${folder.name}`}>
          <EllipsisVertical size={16} aria-hidden />
        </ActionIcon>
      </Menu.Target>
      <Menu.Dropdown>
        {mayChange(folder) ? (
          <Menu.Item
            leftSection={<Pencil size={14} aria-hidden />}
            onClick={() => setEditing({ kind: "rename", folderId: folder.id })}
          >
            Rename
          </Menu.Item>
        ) : null}
        <Menu.Item
          leftSection={<LinkIcon size={14} aria-hidden />}
          closeMenuOnClick={false}
          onClick={() => copyLink(folder)}
        >
          {clipboard.copied && copied === folder.id ? "Link copied" : "Copy link"}
        </Menu.Item>
        {mayChange(folder) ? (
          <Menu.Item leftSection={<FolderInput size={14} aria-hidden />} onClick={() => setMoving(folder)}>
            Move
          </Menu.Item>
        ) : null}
        <Menu.Item
          leftSection={<FolderPlus size={14} aria-hidden />}
          onClick={() => {
            onFavorites(false);
            setEditing({ kind: "child", parentId: folder.id });
          }}
        >
          Add subproject
        </Menu.Item>
        <Menu.Item
          leftSection={<Star size={14} aria-hidden />}
          onClick={() => toggleFavorite(folder)}
        >
          {folder.is_favorite ? "Remove from favourites" : "Add to favourites"}
        </Menu.Item>
        {mayChange(folder) ? (
          <Menu.Item
            color="red"
            leftSection={<Trash2 size={14} aria-hidden />}
            onClick={() => setDeleting(folder)}
          >
            Delete
          </Menu.Item>
        ) : null}
      </Menu.Dropdown>
    </Menu>
  );

  const submitEdit = (name: string) => {
    if (editing?.kind === "rename") {
      rename.mutate({ ...ids, folderId: editing.folderId, data: { name } });
    } else if (editing?.kind === "child") {
      create.mutate({ ...ids, data: { name, parent_id: editing.parentId } });
    }
  };
  const shownTree = favorites ? favoriteTree : tree;
  const noFavorites = favorites && folders.isSuccess && favoriteTree.children(null).length === 0;

  return (
    <div className={classes.panel}>
      <ScrollArea offsetScrollbars className={classes.panelScroll}>
        <nav aria-label="Projects" className={classes.panelList}>
          {folders.isError ? <ErrorAlert error={folders.error} /> : null}
          {failed === null ? null : <ErrorAlert error={failed} />}
          {noFavorites ? (
            <Text size="sm" className={classes.noFavorites}>
              You don't have favourites project
            </Text>
          ) : (
            <>
              <UnstyledButton
                className={classes.folder}
                aria-pressed={folderId === undefined}
                onClick={() => onChoose(undefined)}
              >
                <LayoutGrid size={16} strokeWidth={1.5} aria-hidden />
                <span className={classes.folderName}>All assets</span>
              </UnstyledButton>
              {folders.isSuccess ? (
                <FolderTreeList
                  tree={shownTree}
                  selectedId={folderId}
                  onSelect={(folder) => onChoose(folder.id)}
                  rowStart={star}
                  rowEnd={menu}
                  pinnedId={menuFor}
                  editing={editing}
                  onEdit={submitEdit}
                  onEditCancel={() => {
                    // A cancelled edit leaves no refusal behind.
                    create.reset();
                    rename.reset();
                    setEditing(undefined);
                  }}
                  editPending={create.isPending || rename.isPending}
                  label="Projects"
                />
              ) : null}
            </>
          )}
          {favorites ? null : (
            <>
              {creating ? (
                <FolderNameField
                  initial=""
                  placeholder="New project"
                  label="New project"
                  pending={create.isPending}
                  onSubmit={(name) => create.mutate({ ...ids, data: { name } })}
                  onCancel={() => {
                    create.reset();
                    setCreating(false);
                  }}
                />
              ) : null}
              <Button
                variant="light"
                mt={24}
                leftSection={<Plus size={16} strokeWidth={1.5} aria-hidden />}
                onClick={() => setCreating(true)}
              >
                Create Project
              </Button>
            </>
          )}
          <MoveFolderDialog
            {...ids}
            tree={tree}
            folder={moving}
            onClose={() => setMoving(undefined)}
            onMoved={async () => {
              await refresh();
            }}
          />
          <DeleteFolderDialog
            {...ids}
            tree={tree}
            folder={deleting}
            onClose={() => setDeleting(undefined)}
            onDeleted={async (gone) => {
              if (folderId !== undefined && gone.includes(folderId)) onChoose(undefined);
              // "Delete all" sends files to the trash and "keep" refiles them: the lists change too.
              await Promise.all([refresh(), refreshLibraryLists(queryClient, teamId, brandId)]);
            }}
          />
        </nav>
      </ScrollArea>
    </div>
  );
}
